import * as UseFeatureTracker from "@omni/services/tracking/trackers/feature/useFeature.tracker";
import * as ReviewImpressionTracker from "@omni/services/tracking/trackers/impression/reviewImpression.tracker";
import { type Review as TReview } from "@omni/types/api/reviews/bff/response/review.model";
import { cn } from "@omni/utils/styles";
import { isEmpty } from "lodash-es";
import { useState } from "react";
import { useInView } from "react-intersection-observer";

import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { useReviewsContext } from "../context/reviews.context";
import { Filters } from "./components/filters/filters.component";
import { Review } from "./components/review/review.component";
import { Reviewer } from "./components/reviewer/reviewer.component";
import { ReviewerModal } from "./components/reviewerModal/reviewerModal.container";
import { SecondaryRating } from "./components/secondaryRating/secondaryRating.component";
import { ShowMore } from "./components/showMore/showMore.component";

type ReviewsComponentProps = {
    id: string;
    range: number;
    reviews: Array<TReview>;
};

type ReviewRowProps = {
    range: number;
    review: TReview;
};

const ReviewRow = ({ range, review }: ReviewRowProps) => {
    const { sku, brand, category } = useReviewsContext();

    const [reviewerModalOpen, setReviewerModalOpen] = useState<boolean>(false);

    const handleOnInView = (inView: boolean) => {
        if (!inView) return;

        ReviewImpressionTracker.track({
            contentId: review.id,
            productId: sku,
            brand: brand,
            categoryId: category
        });
    };

    const [inviewRef] = useInView({
        triggerOnce: true,
        onChange: handleOnInView
    });

    const handleOnReviewerClick = () => {
        UseFeatureTracker.track({
            type: "bazaarvoice",
            name: "profile",
            sku: sku,
            brand: brand,
            categoryId: category
        });

        setReviewerModalOpen(true);
    };

    return (
        <section
            ref={inviewRef}
            className={cn(
                "tw:flex tw:flex-col tw:relative",
                "tw:not-first:pt-8 tw:not-first:border-t tw:not-first:border-grey-light",
                "tw:md:flex-row! tw:md:flex-wrap",
                "tw:lg:border-t-0!"
            )}
        >
            <div
                className={cn(
                    "tw:md:flex-1/4 tw:md:grow-0 tw:md:shrink-0 tw:md:pr-12",
                    "tw:xl:flex-3/20"
                )}
            >
                <Reviewer
                    reviewer={review.author}
                    range={range}
                    onClick={handleOnReviewerClick}
                />
            </div>
            <div
                className={cn(
                    "tw:md:flex-3/4 tw:md:grow-0 tw:md:shrink-0",
                    "tw:xl:flex-13/20 tw:xl:pr-12"
                )}
            >
                <Review
                    range={range}
                    review={review}
                    showLink={false}
                    showProductImage={false}
                />
            </div>
            <div
                className={cn(
                    "tw:md:flex-3/4 tw:md:grow-0 tw:md:shrink-0 tw:md:ml-[25%]",
                    "tw:xl:flex-4/20 tw:xl:ml-0",
                    { "tw:mt-6": review.secondaryRating }
                )}
            >
                {review.secondaryRating && (
                    <SecondaryRating secondaryRating={review.secondaryRating} />
                )}
            </div>
            <ReviewerModal
                range={range}
                reviewer={review.author}
                open={reviewerModalOpen}
                toggleHandler={setReviewerModalOpen}
            />
        </section>
    );
};

export const ReviewsComponent = ({
    id,
    range,
    reviews
}: ReviewsComponentProps) => {
    const { t } = useGranite();

    return (
        <div
            id={id}
            className={cn("tw:flex tw:flex-col tw:gap-8 tw:relative")}
        >
            <div className={cn("tw:sticky tw:top-0 tw:z-10")}>
                <Filters />
            </div>
            <div className={cn("tw:flex tw:flex-col tw:gap-8", "tw:lg:gap-12")}>
                {reviews.map((review) => (
                    <ReviewRow
                        key={review.id}
                        range={range}
                        review={review}
                    />
                ))}
                {isEmpty(reviews) && t("product.reviews.noReviews")}
            </div>
            <div className={cn("tw:mt-4")}>
                <ShowMore />
            </div>
        </div>
    );
};
